import {
  Card,
  Segmented,
  Skeleton,
  Switch,
  Typography,
  Dropdown,
  Button,
  Input,
  Modal, Space,
} from "antd";
import { useRef, useState, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import {
  DownloadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileJpgOutlined,
} from "@ant-design/icons";
import { exportChartAsPDF } from "../../../utils/export.utils";
import { LOGOS } from "../../../utils/constant";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import dayjs from "dayjs";

const { Title } = Typography;

const EngagementChartPanel = ({
  loading,
  chartData,
  period,
  setPeriod,
  chartType,
  setChartType,
  isStacked,
  setIsStacked,
  groupBy,
  setGroupBy,
  dateRange,
  userName,
    // displayMode,
    // setDisplayMode,
  handleZoom,
  resetZoom,
  isZoomed,
}) => {
  const chartRef = useRef();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [footnote, setFootnote] = useState("");
  const { username } = useAuthUser()

  const filenamePrefix = `engagement-${groupBy}-${period}-${username}`;

  // Format dates for the zoom slider
  const formatZoomLabel = useCallback((value) => {
    return dayjs(value).format(period === 'Year' ? 'YYYY' :
                             period === 'Month' ? 'MMM YY' :
                             period === 'Week' ? 'MMM D' : 'D MMM');
  }, [period]);


  const getOption = () => {
    const option = {
      title: [
        {
          text: "Engagement Over Time",
          left: "center",
          top: 0,
          textStyle: {
            fontSize: 18,
            fontWeight: "bold",
            color: "#333",
          },
        },
        {
          text: `${dateRange[0].format("D MMM YYYY")} - ${dateRange[1].format(
            "D MMM YYYY"
          )}${isZoomed ? " (Zoomed)" : ""}`,
          left: "center",
          top: 30,
          textStyle: {
            fontSize: 14,
            color: "#666",
          },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
      },
      legend: {
        type: "scroll",
        position: "top",
        top: 60,
        left: 24,
        orient: "horizontal",
        itemGap: 24,
        itemWidth: 18,
        itemHeight: 14,
        textStyle: {
          fontSize: 14,
          fontWeight: 400,
          color: "#444",
          padding: [0, 8, 0, 0],
        },
        icon: "circle",
        padding: [0, 0, 0, 0],
        data: chartData?.series?.map((s) => s.name) || [],
      },
      toolbox: {
        show: true,
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Zoom',
              back: 'Reset Zoom'
            }
          },
          // saveAsImage: {
          //   title: "Save as Image",
          //   pixelRatio: 2,
          // },
          restore: {
            title: 'Reset View'

          }
        },
        right: 20,
        top: 20,
      },
      grid: {
        top: 110,
        left: "3%",
        right: "4%",
        bottom: "15%", // Increased bottom margin for data zoom
        containLabel: true,
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 50,
          height: 20,
          bottom: 10,
          handleSize: '80%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.6)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          },
          labelFormatter: (value) => formatZoomLabel(chartData?.buckets?.[value]),
          brushSelect: true,
          zoomLock: false,
          filterMode: 'filter'
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: false
        }
      ],
      xAxis: {
        type: "category",
        boundaryGap: chartType === "bar",
        data: chartData?.buckets || [],
        axisLabel: {
          formatter: (value) => formatZoomLabel(value),
          rotate: chartData?.buckets?.length > 20 ? 45 : 0,
          interval: 'auto'
        },
      },
      yAxis: {
        type: "value",
        name: chartData?.yAxisName || "Clicks",
      },
      series:
        chartData?.series?.map((series) => ({
          ...series,
          type: chartType === "bar" ? "bar" : "line",
          stack: isStacked ? "total" : undefined,
          areaStyle:
            chartType !== "bar" && isStacked
              ? {
                  opacity: 0.8,
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: series.itemStyle?.color || "#1890ff" },
                    { offset: 1, color: "rgba(255, 255, 255, 0.1)" },
                  ]),
                }
              : undefined,
          emphasis: {
            focus: "series",
          },
          label: {
            show: chartType === "bar",
            position:
              chartType === "bar" ? (isStacked ? "inside" : "top") : undefined,
          },
          barWidth: chartType === "bar" ? "60%" : undefined,
          lineStyle:
            chartType !== "bar"
              ? {
                  width: 2,
                }
              : undefined,
          showSymbol: chartType !== "bar",
          smooth: true,
          animationDuration: 1000,
          animationEasing: 'cubicOut'
        })) || [],
    };

    // Add zoom event handler
    option.dataZoom[0].onChange = (params) => {
      const { startValue, endValue } = params;
      if (chartData?.buckets?.length) {
        const startDate = chartData.buckets[startValue];
        const endDate = chartData.buckets[endValue];
        if (startDate && endDate) {
          handleZoom([dayjs(startDate), dayjs(endDate)]);
        }
      }
    };


    return option;
  };

  const handleChartReady = (echartsInstance) => {
    // Reset zoom when data changes
    echartsInstance.on('restore', () => {
      resetZoom();
    });
  };

  const handleExportImage = async (format = "png") => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (chartInstance) {
      const dataURL = chartInstance.getDataURL({
        type: format,
        pixelRatio: 2,
        backgroundColor: "#fff",
      });
      const link = document.createElement("a");
      link.href = dataURL;
      link.download = `${filenamePrefix}.${format}`;
      link.click();
    }
  };

  const handleExportPDF = async () => {
    const chartNode = chartRef.current?.getEchartsInstance();
    await exportChartAsPDF({
      chartElement: chartNode,
      filename: `${filenamePrefix}.pdf`,
      footnote,
      logos: LOGOS,
    });
    setIsModalVisible(false);
    setFootnote("");
  };

  const exportMenuItems = [
    {
      key: "png",
      icon: <FileImageOutlined />,
      label: "Export as PNG",
      onClick: () => handleExportImage("png"),
    },
    {
      key: "jpeg",
      icon: <FileJpgOutlined />,
      label: "Export as JPEG",
      onClick: () => handleExportImage("jpeg"),
    },
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: "Export as PDF with Footnote",
      onClick: () => setIsModalVisible(true),
    },
  ];

  return (
    <Card
      title={
        <>
          {/* <Title level={4}>Engagement Over Time</Title>
          <Title level={5} type="secondary">
            {`${dateRange[0].format("D MMM YYYY")} - ${dateRange[1].format(
              "D MMM YYYY"
            )}${isZoomed ? " (Zoomed)" : ""}`}
          </Title> */}
        </>
      }
      extra={
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
          <Space
              style={{width: "100%", justifyContent: "space-between", marginBottom: 12}}
              wrap
          >
          <Segmented
            size="small"
            value={period}
            onChange={setPeriod}
            options={["Day", "Week", "Month", "Year"]}
          />
          <Segmented
            size="small"
            value={groupBy}
            onChange={setGroupBy}
            options={[
              { label: "By Condition", value: "condition" },
              { label: "By Designation", value: "designation" },
              { label: "By Hospital",    value: "hospital" },
              { label: "By District",    value: "district" },
              { label: "By Gender",      value: "gender" },
            ]}
          />

          <Dropdown
            menu={{ items: exportMenuItems }}
            placement="bottomRight"
            arrow
          >
            <Button icon={<DownloadOutlined />} />
          </Dropdown>
          </Space>
        </div>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <ReactECharts
          key={`${groupBy}-${chartType}-${isStacked}`}
          ref={chartRef}
          option={getOption()}
          style={{ height: 500 }} // Increased height for zoom control
          theme="light"
          onChartReady={handleChartReady}
          onEvents={{
            'dataZoom': (params) => {
              // Handle continuous zooming
              if (params.batch) {
                const startValue = params.batch[0].startValue;
                const endValue = params.batch[0].endValue;
                if (chartData?.buckets?.length) {
                  const startDate = chartData.buckets[startValue];
                  const endDate = chartData.buckets[endValue];
                  if (startDate && endDate) {
                    handleZoom([dayjs(startDate), dayjs(endDate)]);
                  }
                }
              }
            }
          }}
        />
      )}
      <Modal
        open={isModalVisible}
        title="Enter extra information for PDF export"
        onCancel={() => setIsModalVisible(false)}
        onOk={handleExportPDF}
      >
        <Input.TextArea
          rows={4}
          value={footnote}
          onChange={(e) => setFootnote(e.target.value)}
          placeholder="Add descriptive footnote text here..."
        />
      </Modal>
    </Card>
  );
};

export default EngagementChartPanel;